import type { ISubAction } from 'pages/Batch/utils/analytics';

import effectsData from './data/effects-list.json';
import categorySortOrder from './data/category-sort-order.json';
import thumbnailOverrides from './data/thumbnail-override.json';
import overlaysData from './data/overlays.json';

export const DEFAULT_WFX_INTENSITY = 35;
export const DEFAULT_OVERLAY_INTENSITY = 100;

export const NONE_THUMBNAIL = 'https://cdn-effects-thumbnails.picsart.com/thumbnails/f3c6dd59-c929-4161-a89e-e1fb04f4b416.png';

export type EffectEngine = 'video-sdk' | 'editor-lib';

export interface IWfxEffect {
  id: string;
  name: string;
  thumbUrl: string;
  license?: string;
}

export interface IWfxCategory {
  category: string;
  engine: EffectEngine;
  editorLibCategoryId?: string;
  effects: IWfxEffect[];
}

const thumbOverridesMap = thumbnailOverrides as Record<string, string>;

const rawCategories = effectsData as Array<{
  category: string;
  engine: string;
  editorLibCategoryId?: string;
  effects: Array<{ id: string; name: string; thumbUrl: string; license?: string }>;
}>;

const categoryMap = new Map(rawCategories.map(cat => [cat.category, cat]));

export const WFX_CATEGORIES: IWfxCategory[] = (categorySortOrder as string[])
  .filter(name => categoryMap.has(name))
  .map(name => {
    const cat = categoryMap.get(name)!;
    return {
      category: cat.category,
      engine: cat.engine as EffectEngine,
      editorLibCategoryId: cat.editorLibCategoryId,
      effects: cat.effects.map(effect => ({
        ...effect,
        thumbUrl: thumbOverridesMap[effect.id] || effect.thumbUrl,
      })),
    };
  });

// --- Overlays ---

export interface IOverlayItem {
  resource_id: string;
  title: string;
  icon_url: string;
  resource_url: string;
  blendMode: string;
  license?: string;
}

export interface IOverlayCategory {
  category_id: string;
  localized_package_name: string;
  items: IOverlayItem[];
}

const rawOverlays = (overlaysData as { data: IOverlayCategory[] }).data;

export const OVERLAY_CATEGORIES: IOverlayCategory[] = rawOverlays;

export function buildWfxSubTools(selectedEffect: IWfxEffect | null, selectedOverlayId: string | null): ISubAction[] {
  const result: ISubAction[] = [];

  if (selectedEffect) {
    const category = WFX_CATEGORIES.find(c => c.effects.some(e => e.id === selectedEffect.id));
    if (category) {
      result.push({
        name: category.category,
        value: { item: selectedEffect.name, item_id: selectedEffect.id },
      });
    }
  }

  if (selectedOverlayId) {
    const overlayMatch = OVERLAY_CATEGORIES.map(cat => ({ cat, overlay: cat.items.find(o => o.resource_id === selectedOverlayId) })).find(({ overlay }) => !!overlay);
    if (overlayMatch?.overlay) {
      result.push({
        name: overlayMatch.cat.localized_package_name,
        value: { item: overlayMatch.overlay.title, item_id: overlayMatch.overlay.resource_id },
      });
    }
  }

  return result;
}
